/**
 * The text operations behind `edit_file` and a line range of `read_file`.
 *
 * `edit_file` replaces an exact string, as the Edit tools of Claude Code and
 * Gemini CLI do, since that is the edit agents are trained to make. The
 * string must occur once, unless every occurrence is to be replaced, so an
 * edit never lands on a place the caller did not mean. Agents write line ends
 * as \n; in a file with \r\n line ends, both strings get \r\n before matching.
 */

export type Replacement =
    { ok: true; text: string; count: number; lines: [number, number][] } | { ok: false; error: string };

/** How many start lines an error about several occurrences lists. */
const LINES_LISTED = 10;

export function replaceText(text: string, oldString: string, newString: string, replaceAll: boolean): Replacement {
    if (oldString === "") {
        return { ok: false, error: "old_string is empty. write_file creates or replaces a file." };
    }
    if (oldString === newString) {
        return { ok: false, error: "old_string and new_string are the same. Nothing was changed." };
    }
    if (text.includes("\r\n")) {
        oldString = withCrlf(oldString);
        newString = withCrlf(newString);
    }
    const found: number[] = [];
    let at = text.indexOf(oldString);
    while (at !== -1) {
        found.push(at);
        at = text.indexOf(oldString, at + oldString.length);
    }
    if (found.length === 0) {
        return {
            ok: false,
            error:
                "old_string does not occur in the file. It has to match exactly, including " +
                "indentation; read_file shows the current content.",
        };
    }
    if (found.length > 1 && !replaceAll) {
        const starts = found.slice(0, LINES_LISTED).map((at) => lineOf(text, at));
        const more = found.length > LINES_LISTED ? ", …" : "";
        return {
            ok: false,
            error:
                `old_string occurs ${found.length} times, starting on lines ` +
                `${starts.join(", ")}${more}. Include more of the surrounding lines to pick one, ` +
                "or set replace_all to replace every occurrence. Nothing was changed.",
        };
    }
    const parts: string[] = [];
    const lines: [number, number][] = [];
    let from = 0;
    let startLine = 1;
    for (const at of found) {
        const before = text.slice(from, at);
        parts.push(before, newString);
        startLine += count(before, "\n");
        lines.push([startLine, startLine + count(newString.replace(/\r?\n$/, ""), "\n")]);
        startLine += count(newString, "\n");
        from = at + oldString.length;
    }
    parts.push(text.slice(from));
    return { ok: true, text: parts.join(""), count: found.length, lines };
}

/** Lines `offset` (from 1) on, at most `limit` of them, with their line ends. */
export function sliceLines(
    text: string,
    offset: number,
    limit: number | undefined,
): { text: string; first: number; last: number; total: number } | { error: string } {
    const lines = text.split(/(?<=\n)/);
    const total = text === "" ? 0 : lines.length;
    if (offset > total) {
        return { error: `The file has ${total} lines, fewer than offset ${offset}.` };
    }
    const end = limit === undefined ? total : Math.min(total, offset - 1 + limit);
    return { text: lines.slice(offset - 1, end).join(""), first: offset, last: end, total };
}

function withCrlf(text: string): string {
    return text.replace(/\r?\n/g, "\r\n");
}

function lineOf(text: string, at: number): number {
    return count(text.slice(0, at), "\n") + 1;
}

function count(text: string, char: string): number {
    let n = 0;
    for (let at = text.indexOf(char); at !== -1; at = text.indexOf(char, at + 1)) n++;
    return n;
}
